import { disableContentPromotionGoalsCost } from '../../../utils/disableContentPromotionGoalsCost';
import { disableContentPromotionMetrics } from '../../../utils/disableContentPromotionMetrics';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { WithHint } from '../../feedback/Tooltip';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';
import { ContentPromotionBudgetGoals } from './parts/ContentPromotionBudgetGoals/ContentPromotionBudgetGoals';
import { ContentPromotionBudgetMetrics } from './parts/ContentPromotionBudgetMetrics/ContentPromotionBudgetMetrics';
import { ContentPromotionBudgetStrategy } from './parts/ContentPromotionBudgetStrategy/ContentPromotionBudgetStrategy';
import { ContentPromotionBudgetWeek } from './parts/ContentPromotionBudgetWeek/ContentPromotionBudgetWeek';
import { Wrapper } from './styles';
import { ContentPromotionBudgetProps } from './types';

export const ContentPromotionBudget: React.FC<ContentPromotionBudgetProps> = ({
    counters,
    goals,
    selectedCounters,
    selectedGoals,
    weeklyLimit,
    onSelectCounter,
    onRemoveCounter,
    onSelectGoals,
    onRemoveGoal,
    onChangeGoalBudget,
    onChangeWeeklyLimit,
    strategyType,
    payFor,
    budgetType,
    spendLimitType,
    spendLimitAmount,
    spendLimitOptions,
    onChangeStrategyType,
    onChangePayFor,
    onChangeBudgetType,
    onChangeSpendLimitType,
    onChangeSpendLimitAmount,
    ...other
}) => {
    const t = useTranslation();
    const isStrategyEnabled = Number(useExperimentFlag('content_promotion_strategy')) === 1;
    const hiddenBudgetValue = isStrategyEnabled && disableContentPromotionGoalsCost(spendLimitType);
    const hiddenMetrics =
        isStrategyEnabled && disableContentPromotionMetrics(strategyType, spendLimitType);

    return (
        <Wrapper {...other}>
            <Flex direction="row" alignItems="center">
                <Typography variant="title3" weight="bold">
                    {t('Бюджет на продвижение')}
                </Typography>
                <WithHint
                    ml={1}
                    iconColor="var(--color-secondary)"
                    message={t(
                        'Рекомендуемый минимальный бюджет на неделю рассчитывается как 10*ценность цели. Если ценность 5 000 руб, то рекомендуемый недельный бюджет 50 000 руб.',
                    )}
                />
            </Flex>

            {isStrategyEnabled ? (
                <ContentPromotionBudgetStrategy
                    pt={2}
                    strategyType={strategyType}
                    payFor={payFor}
                    budgetType={budgetType}
                    spendLimitType={spendLimitType}
                    spendLimitAmount={spendLimitAmount}
                    weeklyLimit={weeklyLimit}
                    selectedGoals={selectedGoals}
                    spendLimitOptions={spendLimitOptions}
                    onChangeStrategyType={onChangeStrategyType}
                    onChangePayFor={onChangePayFor}
                    onChangeBudgetType={onChangeBudgetType}
                    onChangeSpendLimitType={onChangeSpendLimitType}
                    onChangeSpendLimitAmount={onChangeSpendLimitAmount}
                    onChangeWeeklyLimit={onChangeWeeklyLimit}
                />
            ) : (
                <Typography variant="body" pt={2}>
                    {t(
                        'Стратегия продвижения нацелена на максимум конверсий. Оплата происходит за конверсии. Укажите бюджет, который вы хотите потратить на неделю',
                    )}
                </Typography>
            )}
            {hiddenMetrics ? null : (
                <>
                    <ContentPromotionBudgetMetrics
                        pt={2}
                        counters={counters}
                        selectedCounters={selectedCounters}
                        onSelectCounter={onSelectCounter}
                        onRemoveCounter={onRemoveCounter}
                    />
                    <ContentPromotionBudgetGoals
                        pt={2}
                        goals={goals}
                        selectedCounters={selectedCounters}
                        selectedGoals={selectedGoals}
                        hiddenBudgetValue={hiddenBudgetValue}
                        onSelect={onSelectGoals}
                        onRemove={onRemoveGoal}
                        onChange={onChangeGoalBudget}
                    />
                    {weeklyLimit && (!selectedGoals || !Object.keys(selectedGoals).length) ? (
                        <Typography variant="caption" color="error" mt={1}>
                            {t('Необходимо добавить цель')}
                        </Typography>
                    ) : null}
                </>
            )}
            {!isStrategyEnabled && (
                <ContentPromotionBudgetWeek
                    mt={2}
                    value={weeklyLimit}
                    selectedGoals={selectedGoals}
                    onChange={onChangeWeeklyLimit}
                />
            )}
        </Wrapper>
    );
};
